import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sun, Moon, Check, Loader2, ArrowLeft, X } from 'lucide-react';

function PricingPage() {
  const navigate = useNavigate();
  const authCtx = useAuth();
  const themeCtx = useTheme();
  
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const user = authCtx && authCtx.user;
  const theme = themeCtx && themeCtx.theme;
  const toggle = themeCtx && themeCtx.toggleTheme;

  useEffect(function() {
    api.getPlans().then(setPlans).finally(function() { setLoading(false); });
  }, []);

  const selectPlan = function(id) {
    if (!user) { navigate('/auth?mode=register'); return; }
    if (id === 'free') { toast.info('Already on free plan'); return; }
    setProcessing(id);
    
    api.createOrder(id).then(function(order) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      document.body.appendChild(script);
      script.onload = function() {
        const options = {
          key: order.key_id,
          amount: order.amount,
          currency: 'INR',
          order_id: order.order_id,
          name: 'QueryVerse AI',
          description: order.plan_name + ' Subscription',
          handler: function(response) {
            // Payment successful - verify with backend
            toast.loading('Verifying payment...');
            api.verifyPayment({ 
              razorpay_order_id: response.razorpay_order_id, 
              razorpay_payment_id: response.razorpay_payment_id, 
              razorpay_signature: response.razorpay_signature, 
              plan_id: id 
            })
              .then(function() { 
                toast.dismiss();
                toast.success('Payment successful!');
                navigate('/payment-success'); 
              })
              .catch(function(err) { 
                toast.dismiss();
                toast.error('Payment verification failed. Please contact support.');
                console.error('Verification error:', err);
                setProcessing(null);
              });
          },
          modal: { 
            ondismiss: function() { 
              setProcessing(null);
              toast.info('Payment cancelled');
            },
            escape: true,
            backdropclose: false
          },
          prefill: {
            email: user.email,
            name: user.name
          },
          theme: {
            color: '#7c3aed'
          }
        };
        
        try {
          const rzp = new window.Razorpay(options);
          rzp.on('payment.failed', function(response) {
            toast.error('Payment failed. Please try again.');
            console.error('Payment failed:', response.error);
            setProcessing(null);
          });
          rzp.open();
        } catch (err) {
          toast.error('Could not open payment window. Please try again.');
          console.error('Razorpay error:', err);
          setProcessing(null);
        }
      };
      script.onerror = function() {
        toast.error('Could not load payment system. Please try again.');
        setProcessing(null);
      };
    }).catch(function(err) { 
      toast.error('Could not create order. Please try again.');
      console.error('Order creation error:', err);
      setProcessing(null); 
    });
  };

  const fmt = function(p) { return '\u20B9' + (p/100); };

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/queryverse-logo.png" alt="QueryVerse" className="h-10" />
            <span className="text-[10px] text-muted-foreground hidden sm:block italic">a product by AppCenn Pvt Ltd</span>
          </Link>
          <div className="flex items-center gap-4">
            {toggle && <button onClick={toggle} className="p-2 rounded-full hover:bg-secondary">{theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</button>}
            {user ? <Button variant="ghost" onClick={function() { navigate('/chat'); }} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-2" />Chat</Button> : <Button onClick={function() { navigate('/auth'); }} className="rounded-full">Log in</Button>}
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-semibold">Simple, transparent pricing</h1>
        <p className="mt-4 text-muted-foreground">Choose your plan. All plans have fair-use limits. Longer commitment = more value.</p>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-16">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            
            {/* Free Plan */}
            <div className="p-6 rounded-xl border bg-card">
              <h3 className="font-medium text-muted-foreground">Free</h3>
              <p className="text-3xl font-semibold mt-2">{fmt(0)}</p>
              <p className="text-sm text-muted-foreground mt-1">forever</p>
              <div className="mt-4 py-2 px-3 bg-muted/50 rounded-lg text-center">
                <span className="text-sm font-medium">5 queries/day</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><Check className="h-4 w-4 text-muted-foreground flex-shrink-0" />Basic AI responses</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-muted-foreground flex-shrink-0" />Community support</li>
                <li className="flex gap-2"><X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />Priority responses</li>
                <li className="flex gap-2"><X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />Conversation history</li>
              </ul>
              <Button variant="outline" className="mt-4 w-full rounded-full" disabled={!user || user.subscription_plan === 'free'} onClick={function() { selectPlan('free'); }}>
                {(!user || user.subscription_plan === 'free') ? 'Current Plan' : 'Downgrade'}
              </Button>
            </div>
            
            {/* Daily Pass */}
            <div className="p-6 rounded-xl border bg-card">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Daily Pass</h3>
                <span className="text-[10px] px-2 py-0.5 bg-muted rounded-full text-muted-foreground">Trial</span>
              </div>
              <p className="text-3xl font-semibold mt-2">{fmt(2900)}</p>
              <p className="text-sm text-muted-foreground mt-1">per day</p>
              <div className="mt-4 py-2 px-3 bg-muted/50 rounded-lg text-center">
                <span className="text-sm font-medium">15 queries/day</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" />Standard responses</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" />Email support</li>
                <li className="flex gap-2"><X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />Priority responses</li>
                <li className="flex gap-2"><X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />Conversation history</li>
              </ul>
              <Button variant="outline" className="mt-4 w-full rounded-full" disabled={processing === 'daily' || user?.subscription_plan === 'daily'} onClick={function() { selectPlan('daily'); }}>
                {processing === 'daily' ? <Loader2 className="h-4 w-4 animate-spin" /> : user?.subscription_plan === 'daily' ? 'Current Plan' : 'Select'}
              </Button>
            </div>

            {/* Monthly Plan - Popular */}
            <div className="p-6 rounded-xl border-2 border-primary bg-primary/5 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs rounded-full font-medium">Most Popular</span>
              <h3 className="font-medium">Pro Monthly</h3>
              <p className="text-3xl font-semibold mt-2">{fmt(29900)}</p>
              <p className="text-sm text-muted-foreground mt-1">per month</p>
              <div className="mt-4 py-2 px-3 bg-primary/10 rounded-lg text-center">
                <span className="text-sm font-medium">30 queries/day</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" />Priority responses</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" />Email support</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" />History (30 days)</li>
                <li className="flex gap-2"><X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />Priority human support</li>
              </ul>
              <Button className="mt-4 w-full rounded-full" disabled={processing === 'monthly' || user?.subscription_plan === 'monthly'} onClick={function() { selectPlan('monthly'); }}>
                {processing === 'monthly' ? <Loader2 className="h-4 w-4 animate-spin" /> : user?.subscription_plan === 'monthly' ? 'Current Plan' : 'Select'}
              </Button>
            </div>
            
            {/* Quarterly Plan */}
            <div className="p-6 rounded-xl border bg-card">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Pro Quarterly</h3>
                <span className="text-[10px] px-2 py-0.5 bg-green-500/10 text-green-600 rounded-full">Save 11%</span>
              </div>
              <p className="text-3xl font-semibold mt-2">{fmt(79900)}</p>
              <p className="text-sm text-muted-foreground mt-1">per 3 months</p>
              <div className="mt-4 py-2 px-3 bg-muted/50 rounded-lg text-center">
                <span className="text-sm font-medium">50 queries/day</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" />Priority responses</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" />Full history</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary flex-shrink-0" />Email support</li>
                <li className="flex gap-2"><X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />Priority human support</li>
              </ul>
              <Button variant="outline" className="mt-4 w-full rounded-full" disabled={processing === 'quarterly' || user?.subscription_plan === 'quarterly'} onClick={function() { selectPlan('quarterly'); }}>
                {processing === 'quarterly' ? <Loader2 className="h-4 w-4 animate-spin" /> : user?.subscription_plan === 'quarterly' ? 'Current Plan' : 'Select'}
              </Button>
            </div>

            {/* Yearly Plan - Best Value */}
            <div className="p-6 rounded-xl border-2 border-green-500/50 bg-green-500/5 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-600 text-white text-xs rounded-full font-medium">Best Value</span>
              <h3 className="font-medium">Pro Yearly</h3>
              <p className="text-3xl font-semibold mt-2">{fmt(249900)}</p>
              <p className="text-sm text-muted-foreground mt-1">per year</p>
              <div className="mt-4 py-2 px-3 bg-green-500/10 rounded-lg text-center">
                <span className="text-sm font-medium">50 queries/day</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0" />Priority responses</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0" />Priority human support</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0" />Early access features</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0" />Admin-ready features</li>
              </ul>
              <Button className="mt-4 w-full rounded-full bg-green-600 hover:bg-green-700" disabled={processing === 'yearly' || user?.subscription_plan === 'yearly'} onClick={function() { selectPlan('yearly'); }}>
                {processing === 'yearly' ? <Loader2 className="h-4 w-4 animate-spin" /> : user?.subscription_plan === 'yearly' ? 'Current Plan' : 'Save 30%'}
              </Button>
            </div>
          </div>
        )}

        {/* Fair Use Notice */}
        <div className="mt-8 p-4 rounded-xl bg-muted/50 text-center">
          <p className="text-sm text-muted-foreground">
            <strong>Fair-use policy:</strong> Free (5/day) • Daily Pass (15/day) • Monthly (30/day) • Quarterly & Yearly (50/day max).
            All limits are hard-enforced. No rollover. No exceptions.
          </p>
        </div>

        {/* Enterprise CTA */}
        <div className="mt-6 p-6 rounded-xl border border-dashed border-primary/30 bg-primary/5 text-center">
          <h3 className="font-medium">Need more? Enterprise plans available.</h3>
          <p className="text-sm text-muted-foreground mt-1">Custom limits, SSO, dedicated support, SLA guarantees.</p>
          <Button variant="outline" className="mt-4 rounded-full" onClick={function() { window.location.href = 'mailto:enterprise@queryverse.io'; }}>
            Contact Sales
          </Button>
        </div>
      </div>

      <footer className="py-8 px-6 border-t border-border/50 text-center text-sm text-muted-foreground">
        © 2025 QueryVerse AI - A product by AppCenn Pvt Ltd
      </footer>
    </div>
  );
}

export default PricingPage;
